import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { httpClient } from 'src/libs';
import { serializeErrorMessage } from 'src/utils';

// Types
export interface TimeFilter {
  startDate: string;
  endDate: string;
  groupBy: 'day' | 'week' | 'month';
}

export interface AnalyticsFilters extends TimeFilter {
  channels?: string[];
  status?: string[];
}

export interface MessageStats {
  avg_messages_per_conversation: number;
  avg_user_messages: number;
  avg_assistant_messages: number;
  avg_function_messages: number;
  total_messages: number;
  total_user_messages: number;
  total_assistant_messages: number;
  total_function_messages: number;
  avg_response_time_user_to_assistant_seconds?: number;
  avg_response_time_assistant_to_user_seconds?: number;
  response_time_user_to_assistant_count?: number;
  response_time_assistant_to_user_count?: number;
  response_rate_to_user_percentage?: number;
  response_rate_to_assistant_percentage?: number;
}

export interface TimingStats {
  avg_response_time_user_to_assistant_seconds: number;
  avg_response_time_assistant_to_user_seconds: number;
  response_time_user_to_assistant_count: number;
  response_time_assistant_to_user_count: number;
  response_rate_assistant_to_user_percentage: number;
  response_rate_user_to_assistant_percentage: number;
}

export interface FeedbackStats {
  positive_feedback_count: number;
  negative_feedback_count: number;
  total_feedback_count: number;
  feedback_ratio: number;
}

// Updated to match the actual API response structure
export interface ConversationAnalyticsResponse {
  success: boolean;
  analytics: {
    total_count: number;
    status_distribution: Record<string, number>;
    conversations_by_time: Record<string, number>;
    message_stats: MessageStats;
    feedback_stats: FeedbackStats;
  };
}

export interface MessageAnalyticsResponse {
  success: boolean;
  analytics: {
    total_count: number;
    role_distribution: Record<string, number>;
    status_distribution: Record<string, number>;
    messages_by_time: Record<string, number>;
    timing_stats: TimingStats;
    feedback_stats: FeedbackStats;
  };
}

// Normalized data for UI
export interface ConversationAnalytics {
  totalCount: number;
  statusDistribution: Record<string, number>;
  timeBasedData: Array<{
    period: string;
    count: number;
  }>;
  averageMessagesPerConversation: number;
  averageDuration: number;
  messageStats: MessageStats;
  feedbackStats: FeedbackStats;
}

export interface MessageAnalytics {
  totalCount: number;
  roleDistribution: Record<string, number>;
  statusDistribution: Record<string, number>;
  timeBasedData: Array<{
    period: string;
    count: number;
  }>;
  timingStats: TimingStats;
  feedbackStats: FeedbackStats;
}

export interface StatusChangeAnalyticsResponse {
  success: boolean;
  analytics: {
    total_count: number;
    status_distribution: Record<string, number>;
    changes_by_time?: Record<string, number>;
    changes_by_status?: Record<string, number>;
    changes_by_reason?: Record<string, number>;
  };
}

export interface StatusChangeAnalytics {
  totalCount: number;
  statusDistribution: Record<string, number>;
  timeBasedData?: Array<{
    period: string;
    count: number;
  }>;
  statusBasedData?: Array<{
    status: string;
    count: number;
  }>;
  reasonBasedData?: Array<{
    reason: string;
    count: number;
  }>;
}

interface DataAnalyticsState {
  conversationAnalytics: ConversationAnalytics | null;
  messageAnalytics: MessageAnalytics | null;
  statusChangeAnalytics: StatusChangeAnalytics | null;
  filters: AnalyticsFilters;
  loading: {
    conversations: boolean;
    messages: boolean;
    statusChanges: boolean;
    conversationsCsvExport: boolean;
    messagesCsvExport: boolean;
  };
  error: {
    conversations: string | null;
    messages: string | null;
    statusChanges: string | null;
    conversationsCsvExport: string | null;
    messagesCsvExport: string | null;
  };
}

const initialState: DataAnalyticsState = {
  conversationAnalytics: null,
  messageAnalytics: null,
  statusChangeAnalytics: null,
  filters: {
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    groupBy: 'day',
    channels: [],
    status: []
  },
  loading: {
    conversations: false,
    messages: false,
    statusChanges: false,
    conversationsCsvExport: false,
    messagesCsvExport: false
  },
  error: {
    conversations: null,
    messages: null,
    statusChanges: null,
    conversationsCsvExport: null,
    messagesCsvExport: null
  }
};

export const fetchConversationAnalytics = createAsyncThunk(
  'dataAnalytics/fetchConversationAnalytics',
  async (filters: AnalyticsFilters, thunkAPI) => {
    try {
      const response = await httpClient.get(`${process.env.REACT_APP_BASE_URL}/comms-api/analytics/conversations`, { params: filters });
      const data: ConversationAnalyticsResponse = response.data;

      if (!data.success) {
        return thunkAPI.rejectWithValue('Failed to fetch conversation analytics');
      }

      // Transform the data to match our expected format
      const transformedData: ConversationAnalytics = {
        totalCount: data.analytics.total_count,
        statusDistribution: data.analytics.status_distribution,
        timeBasedData: Object.entries(data.analytics.conversations_by_time || {}).map(([period, count]) => ({
          period,
          count
        })),
        averageMessagesPerConversation: data.analytics.message_stats.avg_messages_per_conversation,
        averageDuration: 0, // Not provided in the API response
        messageStats: data.analytics.message_stats,
        feedbackStats: data.analytics.feedback_stats
      };


      return transformedData;
    } catch (error) {
      const message = serializeErrorMessage(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchMessageAnalytics = createAsyncThunk(
  'dataAnalytics/fetchMessageAnalytics',
  async (filters: AnalyticsFilters, thunkAPI) => {
    try {
      const response = await httpClient.get(`${process.env.REACT_APP_BASE_URL}/comms-api/analytics/messages`, { params: filters });
      const data: MessageAnalyticsResponse = response.data;

      if (!data.success) {
        return thunkAPI.rejectWithValue('Failed to fetch message analytics');
      }

      // Transform the data to match our expected format
      const transformedData: MessageAnalytics = {
        totalCount: data.analytics.total_count,
        roleDistribution: data.analytics.role_distribution,
        statusDistribution: data.analytics.status_distribution,
        timeBasedData: Object.entries(data.analytics.messages_by_time || {}).map(([period, count]) => ({
          period,
          count
        })),
        timingStats: data.analytics.timing_stats,
        feedbackStats: data.analytics.feedback_stats
      };


      return transformedData;
    } catch (error) {
      const message = serializeErrorMessage(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const exportConversationsCSV = createAsyncThunk(
  'dataAnalytics/exportConversationsCSV',
  async (filters: AnalyticsFilters, thunkAPI) => {
    try {
      // Make API call to download CSV
      const response = await httpClient.get(
        `${process.env.REACT_APP_BASE_URL}/comms-api/analytics/conversations_csv`,
        {
          params: filters,
          responseType: 'blob'
        }
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `conversations_${new Date().toISOString().slice(0, 10)}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { success: true, filename };
    } catch (error) {
      const message = serializeErrorMessage(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const exportMessagesCSV = createAsyncThunk(
  'dataAnalytics/exportMessagesCSV',
  async (filters: AnalyticsFilters, thunkAPI) => {
    try {
      // Make API call to download CSV
      const response = await httpClient.get(
        `${process.env.REACT_APP_BASE_URL}/comms-api/analytics/messages_csv`,
        {
          params: filters,
          responseType: 'blob'
        }
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `messages_${new Date().toISOString().slice(0, 10)}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { success: true, filename };
    } catch (error) {
      const message = serializeErrorMessage(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchStatusChangeAnalytics = createAsyncThunk(
  'dataAnalytics/fetchStatusChangeAnalytics',
  async (filters: AnalyticsFilters, thunkAPI) => {
    try {
      const response = await httpClient.get(`${process.env.REACT_APP_BASE_URL}/comms-api/analytics/status_changes`, { params: filters });
      const data: StatusChangeAnalyticsResponse = response.data;

      if (!data.success) {
        return thunkAPI.rejectWithValue('Failed to fetch status change analytics');
      }

      // Transform the data to match our expected format
      const transformedData: StatusChangeAnalytics = {
        totalCount: data.analytics.total_count,
        statusDistribution: data.analytics.status_distribution,
        timeBasedData: data.analytics.changes_by_time
          ? Object.entries(data.analytics.changes_by_time).map(([period, count]) => ({
            period,
            count
          }))
          : undefined,
        reasonBasedData: data.analytics.changes_by_reason
          ? Object.entries(data.analytics.changes_by_reason)
            .map(([reason, count]) => ({
              reason,
              count
            }))
            .sort((a, b) => b.count - a.count) // Sort by count in descending order
          : undefined
      };

      return transformedData;
    } catch (error) {
      const message = serializeErrorMessage(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const dataAnalyticsSlice = createSlice({
  name: 'dataAnalytics',
  initialState,
  reducers: {
    updateFilters: (state, action: PayloadAction<Partial<AnalyticsFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversationAnalytics.pending, (state) => {
        state.loading.conversations = true;
        state.error.conversations = null;
      })
      .addCase(fetchConversationAnalytics.fulfilled, (state, action) => {
        state.loading.conversations = false;
        state.conversationAnalytics = action.payload;
      })
      .addCase(fetchConversationAnalytics.rejected, (state, action) => {
        state.loading.conversations = false;
        state.error.conversations = action.payload as string;
      })
      .addCase(fetchMessageAnalytics.pending, (state) => {
        state.loading.messages = true;
        state.error.messages = null;
      })
      .addCase(fetchMessageAnalytics.fulfilled, (state, action) => {
        state.loading.messages = false;
        state.messageAnalytics = action.payload;
      })
      .addCase(fetchMessageAnalytics.rejected, (state, action) => {
        state.loading.messages = false;
        state.error.messages = action.payload as string;
      })
      .addCase(exportConversationsCSV.pending, (state) => {
        state.loading.conversationsCsvExport = true;
        state.error.conversationsCsvExport = null;
      })
      .addCase(exportConversationsCSV.fulfilled, (state) => {
        state.loading.conversationsCsvExport = false;
      })
      .addCase(exportConversationsCSV.rejected, (state, action) => {
        state.loading.conversationsCsvExport = false;
        state.error.conversationsCsvExport = action.payload as string;
      })
      .addCase(exportMessagesCSV.pending, (state) => {
        state.loading.messagesCsvExport = true;
        state.error.messagesCsvExport = null;
      })
      .addCase(exportMessagesCSV.fulfilled, (state) => {
        state.loading.messagesCsvExport = false;
      })
      .addCase(exportMessagesCSV.rejected, (state, action) => {
        state.loading.messagesCsvExport = false;
        state.error.messagesCsvExport = action.payload as string;
      })
      .addCase(fetchStatusChangeAnalytics.pending, (state) => {
        state.loading.statusChanges = true;
        state.error.statusChanges = null;
      })
      .addCase(fetchStatusChangeAnalytics.fulfilled, (state, action) => {
        state.loading.statusChanges = false;
        state.statusChangeAnalytics = action.payload;
      })
      .addCase(fetchStatusChangeAnalytics.rejected, (state, action) => {
        state.loading.statusChanges = false;
        state.error.statusChanges = action.payload as string;
      });
  }
});

export const { updateFilters, resetFilters } = dataAnalyticsSlice.actions;

export default dataAnalyticsSlice.reducer;
